import React, { useCallback, useEffect, useState } from "react";

import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  IAgendamentoAlterarTipoForm,
  IAgendamentoAlterarTipoFormRHF,
  IAgendamentoCadastroForm,
  IAgendamentoDTO,
  IReagendamentoForm,
  IReagendamentoProps,
} from "../../../types/agendamento";
import { Agendamento } from "../../../services/Agendamento";
import { toast } from "react-toastify";
import { useSessionStorage } from "../../../hooks/useSessionStorage";
import { useContextSite } from "../../../context/Context";
import { TipoAtendimentoEnum } from "../../../enums/tipoAtendimento";
import { StatusAgendamentoEnum } from "../../../enums/statusAgendamento";
import { TipoClienteEnum } from "../../../enums/tipoCliente";
import { watch } from "fs";

type ModalProps = {
  isOpen: boolean;
  reagendamento?: IReagendamentoProps;
};

export const useStore = () => {
  const [searchParams] = useSearchParams();
  const [dataDelivery, setDataDelivery] = useState<IAgendamentoDTO>(
    {} as IAgendamentoDTO
  );
  const reagendamento = !!(searchParams.get("reagendamento") === "true");
  const [token] = useSessionStorage("@token");
  const { setIsLoad } = useContextSite();
  const navigate = useNavigate();
  const [usuario] = useSessionStorage("@usuarioMapa");
  const { uuidAgendamento } = useParams();
  const [modal, setModal] = useState<ModalProps>({ isOpen: false });
  const [uuidLoja, setUuidLoja] = useState("");
  const [revistoria, setRevistoria] = useState(false);
  const [dataLimiteRevistoria, setDataLimiteRevistoria] = useState("");
  const [tipoAtendimento, setTipoAtendimento] = useState<TipoAtendimentoEnum>(
    TipoAtendimentoEnum.LOJA
  );
  const [tipoAtendimentoOriginal, setTipoAtendimentoOriginal] =
    useState<TipoAtendimentoEnum>(TipoAtendimentoEnum.LOJA);

  const getAgendamento = useCallback(() => {
    setIsLoad(true);
    Agendamento.getById({ uuid: uuidAgendamento })
      .then(({ data }) => {
        if (data?.loja?.uuid) {
          setUuidLoja(data?.loja?.uuid);
        }
        if (data?.delivery?.uuid) {
          setDataDelivery(data);
          setTipoAtendimento(TipoAtendimentoEnum.DOMICILIO);
          setTipoAtendimentoOriginal(TipoAtendimentoEnum.DOMICILIO);
        }
        setRevistoria(!!data?.revistoria);
        setDataLimiteRevistoria(data?.dataLimiteRevistoria ?? "");
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      )
      .finally(() => setIsLoad(false));
  }, []);

  function submitReagendamentoForm(data: IReagendamentoProps) {
    const PAYLOAD: IReagendamentoProps = {
      ...data,
      uuidAgendamento: uuidAgendamento,
    };

    setModal({ isOpen: true, reagendamento: PAYLOAD });
  }

  function handleReagendamento() {
    setIsLoad(true);

    setModal({ isOpen: false });

    const {uuidAgendamento, ...rest} = modal?.reagendamento;

    Agendamento.reagendar({ uuid: uuidAgendamento, props: rest })
      .then(({ data }) => {
        toast.success("Reagendamento efetuado com sucesso!");

        setTimeout(() => {
          navigate(`/meus-agendamentos/agendamento?id=${data?.uuid}`);
        }, 2000);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      )
      .finally(() => setIsLoad(false));
  }

  function submitAgendamento(data: IAgendamentoCadastroForm) {
    setIsLoad(true);

    Agendamento.postV2(data)
      .then(({ data }) => {
        if (token) {
          if (usuario?.tipo === TipoClienteEnum.DESPACHANTE) {
            navigate(`/agendamento/${data.uuid}/despachante-cliente`);
            return;
          }

          Agendamento.vincularAgendamentoAoCliente({
            uuid: data.uuid,
            uuidCliente: usuario?.uuidCliente,
          }).then(() => {
            navigate(`/agendamento/${data.uuid}/servicos`);
            return;
          });
        }
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      )
      .finally(() => setIsLoad(false));
  }

  function handleReagendamentoDelivery(data: IReagendamentoForm) {
    setIsLoad(true);

    Agendamento.reagendar({ uuid: uuidAgendamento, props: data })
      .then(({ data }) => {
        toast.success("Reagendamento efetuado com sucesso!");
        navigate(`/meus-agendamentos/agendamento?id=${data?.uuid}`);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      )
      .finally(() => setIsLoad(false));
  }

  function toReagendamentoForm(
    data: IAgendamentoAlterarTipoFormRHF
  ): IReagendamentoForm {
    const isConcessionaria = data.concessionaria === "CONCESSIONARIA";

    return {
      diaAgendado: data.diaAgendado,
      horaAgendada: data.horaAgendada,
      uuidDelivery: data.uuidDelivery,
      uuidLoja,
      concessionaria: isConcessionaria,
      atendimentoConcessionaria: isConcessionaria
        ? {
            nome: data.nome,
            telefone: data.telefone,
            uuidConcessionaria: data.uuidConcessionaria!,
          }
        : undefined,
      atendimentoDomiciliar: isConcessionaria
        ? undefined
        : {
            endereco: data.endereco,
            nome: data.nome,
            telefone: data.telefone,
          },
    };
  }

  function handleRescheduling(data: IAgendamentoAlterarTipoFormRHF) {
    const mudouTipoAtendimento =
      tipoAtendimentoOriginal === TipoAtendimentoEnum.LOJA ||
      data?.concessionaria === "CONCESSIONARIA";

    if (!mudouTipoAtendimento) {
      handleReagendamentoDelivery(toReagendamentoForm(data));
      return;
    }

    const PAYLOAD: IAgendamentoAlterarTipoForm = {
      ...data,
      concessionaria: data?.concessionaria === "CONCESSIONARIA",
      uuid: uuidAgendamento,
    };

    setIsLoad(true);
    Agendamento.alterarTipoAtendimento(PAYLOAD)
      .then(({ data }) => {
        data?.status === StatusAgendamentoEnum.PAGO
          ? navigate(
              `/agendamento/${data?.uuid}/confirmar-horario?reagendamento=true`
            )
          : navigate(`/agendamento/${data?.uuid}/pagamento`);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          toast.error(mensagem);
        }
      )
      .finally(() => {
        setIsLoad(false);
      });
  }

  useEffect(() => {
    getAgendamento();
  }, [uuidAgendamento, reagendamento]);

  return {
    reagendamento,
    submitAgendamento,
    modal,
    setModal,
    submitReagendamentoForm,
    handleReagendamento,
    tipoAtendimento,
    handleRescheduling,
    setTipoAtendimento,
    uuidLoja,
    dataDelivery,
    revistoria,
    dataLimiteRevistoria,
  };
};
